import { Button, Checkbox, Flex, Heading, Input, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormSuccess } from '../../../components/FormSuccess'
import { MaskedInput } from '../../../components/MaskedInput'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { capitalizeName, hasFullName, useAppToast } from '../../../helpers'
import { StudyCreditRequestService } from '../../../services/StudyCreditRequest/StudyCreditRequestService'
import { IStudyCreditOptionRaw, StudyCreditCourseFormat } from '../../../services/StudyCreditRequest/types'
import {
  DECLARATION_AUTHENTICITY_TEXT,
  DECLARATION_NO_NEW_REQUEST_TEXT,
  EDUCATION_FORMAT_LABELS,
  MAX_TOTAL_UPLOAD_SIZE_MB,
  RESPONSIBLE_LABELS
} from './consts'
import { emptyStudy, IStudyFormState, StudyEntryFields } from './StudyEntryFields'

const PHONE_REGEX = /^\(\d{2}\) \d{4,5}-\d{4}$/

const emptyForm = {
  studentName: '',
  responsible: '' as '' | 'student' | 'other',
  responsibleName: '',
  cpf: '',
  rg: '',
  email: '',
  emailConfirmation: '',
  phone: '',
  phoneConfirmation: '',
  educationFormat: '' as '' | 'in_person' | 'live_online',
  courseId: '',
  consultantId: ''
}

export default function StudyCreditSubmitPage() {
  const service = useMemo(() => new StudyCreditRequestService(), [])
  const toast = useAppToast()

  const [consultants, setConsultants] = useState<IStudyCreditOptionRaw[]>([])
  const [coursesByFormat, setCoursesByFormat] = useState<Record<string, IStudyCreditOptionRaw[]>>({})
  const [form, setForm] = useState({ ...emptyForm })
  const [studies, setStudies] = useState<IStudyFormState[]>([emptyStudy()])
  const [declarationNoNewRequest, setDeclarationNoNewRequest] = useState(false)
  const [declarationAuthenticity, setDeclarationAuthenticity] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Falls back to nested keys (e.g. studies.0.work_files.1) so per-file
  // backend errors still show under the field.
  const error = (key: string) =>
    formErrors[key]?.[0] ?? Object.entries(formErrors).find(([k]) => k.startsWith(`${key}.`))?.[1]?.[0]

  useEffect(() => {
    service
      .getConsultants()
      .then(setConsultants)
      .catch(() => toast.error('Erro ao carregar consultores'))
  }, [service, toast])

  // Tracks in-flight requests so concurrent calls for the same format don't fire twice.
  const loadingFormatsRef = useRef(new Set<string>())

  const loadCourses = (format: StudyCreditCourseFormat) => {
    if (coursesByFormat[format] || loadingFormatsRef.current.has(format)) return
    loadingFormatsRef.current.add(format)
    service
      .getCourses(format)
      .then((courses) => setCoursesByFormat((current) => ({ ...current, [format]: courses })))
      .catch(() => toast.error('Erro ao carregar cursos'))
      .finally(() => loadingFormatsRef.current.delete(format))
  }

  const updateStudy = (index: number, study: IStudyFormState) => {
    setStudies((prev) => prev.map((s, i) => (i === index ? study : s)))
  }

  const validate = (): Record<string, string[]> => {
    const errors: Record<string, string[]> = {}

    if (!hasFullName(form.studentName)) errors.student_name = ['Informe o nome e sobrenome do aluno.']
    if (!form.responsible) errors.responsible = ['Informe quem é o responsável pelo aluno.']
    if (form.responsible === 'other' && !hasFullName(form.responsibleName)) {
      errors.responsible_name = ['Informe o nome e sobrenome do responsável.']
    }
    if (form.cpf.length !== 14) errors.cpf = ['O CPF deve estar no formato 000.000.000-00.']
    if (!form.rg.trim()) errors.rg = ['O RG do aluno é obrigatório.']
    if (!form.email.trim()) errors.email = ['O e-mail é obrigatório.']
    else if (form.email !== form.emailConfirmation) {
      errors.email_confirmation = ['A confirmação do e-mail não confere.']
    }
    if (!PHONE_REGEX.test(form.phone)) errors.phone = ['O telefone deve estar no formato (xx) xxxxx-xxxx.']
    else if (form.phone !== form.phoneConfirmation) {
      errors.phone_confirmation = ['A confirmação do telefone não confere.']
    }
    if (!form.educationFormat) errors.education_format = ['O formato de ensino é obrigatório.']
    if (!form.courseId) errors.course_id = ['O curso desejado é obrigatório.']
    if (!form.consultantId) errors.commercial_consultant_id = ['O consultor é obrigatório.']
    if (!declarationNoNewRequest) {
      errors.declaration_no_new_request = ['É necessário aceitar a declaração para enviar.']
    }
    if (!declarationAuthenticity) {
      errors.declaration_authenticity = ['É necessário aceitar a declaração para enviar.']
    }

    studies.forEach((study, i) => {
      const key = (field: string) => `studies.${i}.${field}`
      if (!study.institution) {
        errors[key('institution')] = ['A instituição de ensino é obrigatória.']
        return
      }
      if (study.institution === 'abra') {
        if (!study.abraUnit) {
          errors[key('abra_unit')] = ['A unidade é obrigatória.']
          return
        }
        if (!study.awarenessConfirmed) {
          errors[key('awareness_confirmed')] = ['É necessário marcar a caixa de ciência para continuar.']
        }
        if (study.abraUnit === 'moema_brooklin') {
          if (!study.courseFormat) errors[key('course_format')] = ['O formato do curso é obrigatório.']
          if (!study.courseId) errors[key('course_id')] = ['O curso é obrigatório.']
        }
        if (study.abraUnit === 'extinct_units') {
          if (!study.courseName.trim()) errors[key('course_name')] = ['O nome do curso é obrigatório.']
          if (study.schoolRecordFiles.length === 0) {
            errors[key('school_record_files')] = ['O histórico escolar é obrigatório.']
          }
          if (study.workFiles.length === 0) {
            errors[key('work_files')] = ['Anexe ao menos um trabalho produzido durante o curso.']
          }
        }
      }
      if (study.institution === 'other_institution') {
        if (!study.institutionName.trim()) errors[key('institution_name')] = ['O nome da instituição é obrigatório.']
        if (!study.courseName.trim()) errors[key('course_name')] = ['O nome do curso é obrigatório.']
        if (study.schoolRecordFiles.length === 0) {
          errors[key('school_record_files')] = ['O histórico escolar/certificado/diploma é obrigatório.']
        }
        if (study.workFiles.length === 0) {
          errors[key('work_files')] = ['Anexe ao menos um trabalho produzido durante o curso.']
        }
      }
      if (study.institution === 'self_taught' && study.workFiles.length === 0) {
        errors[key('work_files')] = ['Anexe ao menos um trabalho produzido.']
      }
    })

    const totalSize = studies.reduce(
      (sum, study) => sum + [...study.schoolRecordFiles, ...study.workFiles].reduce((s, f) => s + f.size, 0),
      0
    )
    if (totalSize > MAX_TOTAL_UPLOAD_SIZE_MB * 1024 * 1024) {
      errors.total_size = [
        `O total de arquivos passou de ${MAX_TOTAL_UPLOAD_SIZE_MB}MB. Reduza o tamanho ou a quantidade de arquivos.`
      ]
    }

    return errors
  }

  const buildFormData = (): FormData => {
    const data = new FormData()
    data.append('student_name', form.studentName.trim())
    data.append('responsible', form.responsible)
    if (form.responsible === 'other') data.append('responsible_name', form.responsibleName.trim())
    data.append('cpf', form.cpf)
    data.append('rg', form.rg.trim())
    data.append('email', form.email.trim())
    data.append('email_confirmation', form.emailConfirmation.trim())
    data.append('phone', form.phone)
    data.append('phone_confirmation', form.phoneConfirmation)
    data.append('education_format', form.educationFormat)
    data.append('course_id', form.courseId)
    data.append('commercial_consultant_id', form.consultantId)
    data.append('declaration_no_new_request', '1')
    data.append('declaration_authenticity', '1')

    studies.forEach((study, i) => {
      data.append(`studies[${i}][institution]`, study.institution)
      if (study.institution === 'abra') {
        data.append(`studies[${i}][abra_unit]`, study.abraUnit)
        data.append(`studies[${i}][awareness_confirmed]`, '1')
        if (study.abraUnit === 'moema_brooklin') {
          data.append(`studies[${i}][course_format]`, study.courseFormat)
          data.append(`studies[${i}][course_id]`, study.courseId)
        }
        if (study.abraUnit === 'extinct_units') {
          data.append(`studies[${i}][course_name]`, study.courseName.trim())
        }
      }
      if (study.institution === 'other_institution') {
        data.append(`studies[${i}][institution_name]`, study.institutionName.trim())
        data.append(`studies[${i}][course_name]`, study.courseName.trim())
      }
      study.schoolRecordFiles.forEach((file) => data.append(`studies[${i}][school_record_files][]`, file))
      study.workFiles.forEach((file) => data.append(`studies[${i}][work_files][]`, file))
    })

    return data
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormErrors({})

    const clientErrors = validate()
    if (Object.keys(clientErrors).length > 0) {
      setFormErrors(clientErrors)
      toast.warning('Verifique os campos do formulário', Object.values(clientErrors)[0][0])
      return
    }

    setSubmitting(true)
    try {
      await service.submit(buildFormData())
      setSubmitted(true)
    } catch (response) {
      // The API returns 400 for validation errors (see Exceptions\Handler); 422 kept as a fallback.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = response as any
      const status = err.response?.status
      if ((status === 400 || status === 422) && err.response?.data?.errors) {
        setFormErrors(err.response.data.errors)
        toast.warning('Verifique os campos do formulário')
      } else {
        toast.error('Erro ao enviar a solicitação', response)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <PublicFormLayout title="Aproveitamento de Estudos">
        <FormSuccess
          title="Solicitação enviada!"
          description="Recebemos a sua solicitação de Aproveitamento de Estudos. Nossa equipe fará a análise e entrará em contato."
        />
      </PublicFormLayout>
    )
  }

  return (
    <PublicFormLayout title="Aproveitamento de Estudos">
      <form onSubmit={handleSubmit}>
        <FormField label="Nome Completo do(a) Aluno(a)" error={error('student_name')}>
          <Input
            value={form.studentName}
            onChange={(e) => setForm({ ...form, studentName: e.target.value })}
            onBlur={(e) => setForm((prev) => ({ ...prev, studentName: capitalizeName(e.target.value) }))}
            disabled={submitting}
          />
        </FormField>

        <FormField label="Quem é o responsável pelo aluno?" mt={4} error={error('responsible')}>
          <FormSelect
            isDisabled={submitting}
            value={form.responsible}
            onChange={(value) => setForm({ ...form, responsible: value as typeof form.responsible })}
            options={Object.entries(RESPONSIBLE_LABELS).map(([value, label]) => ({
              value: value,
              label: label
            }))}
            placeholder="Selecione"
          />
        </FormField>

        {form.responsible === 'other' && (
          <>
            <FormField label="Nome completo do responsável" mt={4} error={error('responsible_name')}>
              <Input
                value={form.responsibleName}
                onChange={(e) => setForm({ ...form, responsibleName: e.target.value })}
                onBlur={(e) => setForm((prev) => ({ ...prev, responsibleName: capitalizeName(e.target.value) }))}
                disabled={submitting}
              />
            </FormField>
          </>
        )}

        <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
          <Flex direction="column" flex={1}>
            <FormField label="CPF do(a) Aluno(a)" mt={4} error={error('cpf')}>
              <MaskedInput
                mask="cpf"
                value={form.cpf}
                onChange={(cpf) => setForm({ ...form, cpf })}
                isDisabled={submitting}
              />
            </FormField>
          </Flex>
          <Flex direction="column" flex={1}>
            <FormField label="RG do(a) Aluno(a)" mt={4} error={error('rg')}>
              <Input value={form.rg} onChange={(e) => setForm({ ...form, rg: e.target.value })} disabled={submitting} />
            </FormField>
          </Flex>
        </Flex>

        <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
          <Flex direction="column" flex={1}>
            <FormField label="E-mail" mt={4} error={error('email')}>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={submitting}
              />
            </FormField>
          </Flex>
          <Flex direction="column" flex={1}>
            <FormField label="Confirme o E-mail" mt={4} error={error('email_confirmation')}>
              <Input
                type="email"
                value={form.emailConfirmation}
                onChange={(e) => setForm({ ...form, emailConfirmation: e.target.value })}
                disabled={submitting}
              />
            </FormField>
          </Flex>
        </Flex>

        <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
          <Flex direction="column" flex={1}>
            <FormField label="Telefone" mt={4} error={error('phone')}>
              <MaskedInput
                mask="phone"
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone })}
                isDisabled={submitting}
              />
            </FormField>
          </Flex>
          <Flex direction="column" flex={1}>
            <FormField label="Confirme o Telefone" mt={4} error={error('phone_confirmation')}>
              <MaskedInput
                mask="phone"
                value={form.phoneConfirmation}
                onChange={(phoneConfirmation) => setForm({ ...form, phoneConfirmation })}
                isDisabled={submitting}
              />
            </FormField>
          </Flex>
        </Flex>

        <FormField label="Selecione o formato de ensino" mt={4} error={error('education_format')}>
          <FormSelect
            isDisabled={submitting}
            value={form.educationFormat}
            onChange={(value) => {
              const format = value as typeof form.educationFormat
              setForm({ ...form, educationFormat: format, courseId: '' })
              if (format) loadCourses(format)
            }}
            options={Object.entries(EDUCATION_FORMAT_LABELS).map(([value, label]) => ({
              value: value,
              label: label
            }))}
            placeholder="Selecione"
          />
        </FormField>

        {form.educationFormat && (
          <>
            <FormField label="Qual é o curso que deseja eliminar conteúdos" mt={4} error={error('course_id')}>
              <SearchableSelect
                placeholder="Selecione o curso..."
                value={form.courseId}
                onChange={(value) => setForm({ ...form, courseId: value })}
                options={(coursesByFormat[form.educationFormat] ?? []).map((c) => ({
                  value: String(c.id),
                  label: c.name
                }))}
              />
            </FormField>
          </>
        )}

        <Heading as="h2" mt={8}>
          Estudos Realizados Anteriormente
        </Heading>

        {studies.map((study, index) => (
          <StudyEntryFields
            key={index}
            index={index}
            study={study}
            onChange={updateStudy}
            coursesByFormat={coursesByFormat}
            onNeedCourses={loadCourses}
            error={error}
            disabled={submitting}
          />
        ))}
        {/* Exact lookup only: the error() fallback would also match per-study keys (studies.N.*). */}
        {formErrors['studies']?.[0] && <Text color="red.500">{formErrors['studies'][0]}</Text>}

        <Flex gap={3} mt={3}>
          <Button
            size="sm"
            variant="outline"
            colorScheme="magenta"
            onClick={() => setStudies([...studies, emptyStudy()])}
            isDisabled={submitting}
          >
            + Adicionar novo estudo
          </Button>
          {studies.length > 1 && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={() => setStudies(studies.slice(0, -1))}
              isDisabled={submitting}
            >
              Remover Estudo
            </Button>
          )}
        </Flex>

        <FormField label="Qual consultor te passou informações?" mt={8} error={error('commercial_consultant_id')}>
          <SearchableSelect
            placeholder="Selecione o consultor..."
            value={form.consultantId}
            onChange={(value) => setForm({ ...form, consultantId: value })}
            options={consultants.map((c) => ({ value: String(c.id), label: c.name }))}
          />
        </FormField>

        <FormField mt={6} error={error('declaration_no_new_request')}>
          <Checkbox
            isChecked={declarationNoNewRequest}
            onChange={(e) => setDeclarationNoNewRequest(e.target.checked)}
            isDisabled={submitting}
            alignItems="flex-start"
          >
            <Text fontSize="sm">{DECLARATION_NO_NEW_REQUEST_TEXT}</Text>
          </Checkbox>
        </FormField>

        <FormField mt={4} error={error('declaration_authenticity')}>
          <Checkbox
            isChecked={declarationAuthenticity}
            onChange={(e) => setDeclarationAuthenticity(e.target.checked)}
            isDisabled={submitting}
            alignItems="flex-start"
          >
            <Text fontSize="sm">{DECLARATION_AUTHENTICITY_TEXT}</Text>
          </Checkbox>
        </FormField>

        {error('total_size') && (
          <Text color="red.500" mt={4}>
            {error('total_size')}
          </Text>
        )}

        <Button colorScheme="magenta" type="submit" w="100%" size="lg" mt={6} isLoading={submitting}>
          Enviar
        </Button>
      </form>
    </PublicFormLayout>
  )
}
